import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, BedDouble, Bath, Maximize, Zap, MapPin, Printer, Phone, ChevronLeft, ChevronRight } from "lucide-react";
import { PublicLayout } from "@/components/PublicLayout";
import { api, mediaUrl, formatPrice } from "@/lib/api";
import { useSettings, whatsappLink } from "@/lib/settings";
import { useSeo } from "@/lib/useSeo";

export default function PropertyDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const s = useSettings();
  const [active, setActive] = useState(0);
  const [tab, setTab] = useState("foto");

  const { data: p, isLoading, isError } = useQuery({
    queryKey: ["property", slug],
    queryFn: async () => (await api.get(`/properties/${slug}`)).data,
    retry: false,
  });

  useSeo({
    title: p ? `${p.title} | Edilstudio Immobiliare` : "Immobile | Edilstudio",
    description: p ? (p.meta_description || `${p.type} in ${p.contract.toLowerCase()} a ${p.zone}. ${p.surface || ""} m². ${formatPrice(p.price)}.`) : "",
    canonicalPath: `/immobili/${slug}`,
    jsonLd: p ? { "@context": "https://schema.org", "@type": "Product", name: p.title, description: p.description, offers: { "@type": "Offer", price: p.price || 0, priceCurrency: "EUR" } } : null,
  });

  if (isLoading) return <PublicLayout><div className="py-32 text-center font-mono text-[#8695A6]">Caricamento…</div></PublicLayout>;
  if (isError || !p) return (
    <PublicLayout><div className="py-32 text-center"><p className="text-[#1B2A41] text-xl font-semibold">Immobile non trovato</p><Link to="/immobili" className="text-[#2472C8] font-semibold mt-4 inline-block">← Torna al portale immobili</Link></div></PublicLayout>
  );

  const gallery = tab === "planimetria" ? (p.floorplans || []) : (p.images || []);
  const cur = gallery[active];
  const unavailable = ["Venduto", "Affittato", "Non più disponibile"].includes(p.status);

  return (
    <PublicLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-[#566476] hover:text-[#1B2A41] mb-6" data-testid="back-btn"><ArrowLeft className="w-4 h-4" /> Indietro</button>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="flex gap-2 mb-4">
              <button onClick={() => { setTab("foto"); setActive(0); }} className={`px-4 py-2 rounded-full text-sm font-semibold ${tab === "foto" ? "bg-[#1B2A41] text-white" : "bg-white border border-[#DCE6EF] text-[#1B2A41]"}`} data-testid="tab-foto">Foto ({p.images?.length || 0})</button>
              {p.floorplans?.length > 0 && <button onClick={() => { setTab("planimetria"); setActive(0); }} className={`px-4 py-2 rounded-full text-sm font-semibold ${tab === "planimetria" ? "bg-[#1B2A41] text-white" : "bg-white border border-[#DCE6EF] text-[#1B2A41]"}`} data-testid="tab-planimetria">Planimetria</button>}
            </div>
            <div className="relative aspect-[16/10] rounded-2xl overflow-hidden bg-[#E3ECF3]">
              {cur ? <img src={mediaUrl(cur.url)} alt={p.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[#A9B4C1] font-mono">Nessuna immagine</div>}
              {gallery.length > 1 && (
                <>
                  <button onClick={() => setActive((active - 1 + gallery.length) % gallery.length)} className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 rounded-full p-2 hover:bg-white" data-testid="gallery-prev"><ChevronLeft className="w-5 h-5" /></button>
                  <button onClick={() => setActive((active + 1) % gallery.length)} className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 rounded-full p-2 hover:bg-white" data-testid="gallery-next"><ChevronRight className="w-5 h-5" /></button>
                </>
              )}
            </div>
            {gallery.length > 1 && (
              <div className="flex gap-2 mt-3 overflow-x-auto hide-scrollbar">
                {gallery.map((g, i) => (
                  <button key={i} onClick={() => setActive(i)} className={`w-24 h-16 rounded-lg overflow-hidden shrink-0 border-2 ${i === active ? "border-[#2472C8]" : "border-transparent"}`}>
                    <img src={mediaUrl(g.url)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            <div className="mt-8">
              <h2 className="font-display text-2xl font-bold text-[#1B2A41]">Descrizione</h2>
              <p className="text-[#2C3A4F] mt-3 leading-relaxed whitespace-pre-line">{p.description || "Descrizione non disponibile. Contattaci per maggiori dettagli."}</p>
            </div>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <div className="bg-white rounded-2xl border border-[#DCE6EF] p-6 sticky top-28" data-testid="property-sidebar">
              <div className="flex items-center gap-1.5 text-[#2472C8] text-xs font-mono uppercase tracking-wider"><MapPin className="w-3.5 h-3.5" /> {p.zone}</div>
              <h1 className="font-display text-2xl font-bold text-[#1B2A41] mt-2 leading-tight">{p.title}</h1>
              <div className="flex gap-2 mt-3">
                <span className="bg-[#1B2A41] text-white text-xs font-semibold px-2.5 py-1 rounded-full">{p.contract}</span>
                <span className={`${unavailable ? "bg-[#566476] text-white" : "bg-[#EEF4F9] text-[#1B2A41]"} text-xs font-semibold px-2.5 py-1 rounded-full`} data-testid="detail-status">{unavailable ? "Non più disponibile" : p.status}</span>
              </div>
              <div className="font-mono text-3xl font-semibold text-[#1B2A41] mt-5">{formatPrice(p.price)}</div>

              <div className="grid grid-cols-3 gap-2 mt-5 text-center">
                {[[BedDouble, p.rooms, "locali"], [Bath, p.bathrooms, "bagni"], [Maximize, p.surface, "m²"]].map(([Icon, v, l], i) => (
                  <div key={i} className="bg-[#EEF4F9] rounded-xl py-3">
                    <Icon className="w-4 h-4 mx-auto text-[#2472C8]" />
                    <div className="font-mono font-semibold text-[#1B2A41] mt-1">{v || "—"}</div>
                    <div className="text-[11px] text-[#566476]">{l}</div>
                  </div>
                ))}
              </div>
              {p.energy_class && <div className="mt-3 flex items-center gap-2 text-sm text-[#566476]"><Zap className="w-4 h-4 text-[#E0961C]" /> Classe energetica <strong className="text-[#1B2A41]">{p.energy_class}</strong></div>}
              {p.address && <div className="mt-2 text-sm text-[#566476]">{p.address}</div>}

              {unavailable ? (
                <div className="mt-6 bg-[#F4F6F9] border border-[#DCE6EF] rounded-xl p-5 text-center" data-testid="detail-unavailable-note">
                  <p className="font-semibold text-[#1B2A41]">Immobile non più disponibile</p>
                  <p className="text-sm text-[#566476] mt-1.5">Questo immobile è stato {p.contract?.toLowerCase().startsWith("affit") ? "affittato" : "venduto"} e non è più in trattativa.</p>
                </div>
              ) : (
                <div className="space-y-2 mt-6">
                  <a href={whatsappLink(s.whatsapp_number, `Salve, sono interessato all'immobile: ${p.title}`)} target="_blank" rel="noopener noreferrer" className="w-full bg-[#25D366] text-white font-semibold py-3 rounded-full flex items-center justify-center gap-2 hover:opacity-90 transition-opacity" data-testid="detail-whatsapp">Richiedi visita su WhatsApp</a>
                  <a href={`tel:${s.phone}`} className="w-full bg-[#1B2A41] text-white font-semibold py-3 rounded-full flex items-center justify-center gap-2 hover:bg-[#24344F] transition-colors" data-testid="detail-call"><Phone className="w-4 h-4" /> {s.phone}</a>
                  <button onClick={() => window.print()} className="w-full border border-[#DCE6EF] text-[#1B2A41] font-semibold py-3 rounded-full flex items-center justify-center gap-2 hover:bg-[#EEF4F9] transition-colors" data-testid="detail-print"><Printer className="w-4 h-4" /> Stampa scheda</button>
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>
    </PublicLayout>
  );
}
